import SearchBar from "@design-system/Inputs/SearchBar";
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useAtom, useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Indicator } from "../../../../../generated/client/graphql";
import ButtonRegular from "../../../../components/Button/ButtonRegular";
import {
    CenterModal,
    CenterModalContent,
} from "../../../../components/Modal/CenterModal";
import DataTable from "../../../../components/Table/DataTable";
import {
    inactiveIndicatorsModalIsOpenAtom,
    selectedInactiveFrameworkIdsAtomFamily,
    selectedInactiveSubthemesAtomFamily,
} from "../../context";
import {
    useInactiveIndicators,
    useUpdateManyIndicatorEntityConfig,
} from "../../data";
import "../../i18n";
import { inactiveIndicatorsTableColumns } from "../IndicatorsTable/columns";
import { selectedTierAtom } from "../IndicatorsTable/ctx";

export default function InactiveIndicatorsModal() {
    const { org_uname } = useParams();
    const { t } = useTranslation("ReportingPage");
    const [open, setOpen] = useAtom(inactiveIndicatorsModalIsOpenAtom);

    const data = useInactiveIndicators();
    const { mutate, isPending } = useUpdateManyIndicatorEntityConfig();
    const selectedSubthemesIds = useAtomValue(
        selectedInactiveSubthemesAtomFamily(org_uname),
    );
    const selectedFrameworkIds = useAtomValue(
        selectedInactiveFrameworkIdsAtomFamily(org_uname),
    );

    const [rowSelection, setRowSelection] = useState({});
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const [searchFilter, setSearchFilter] = useState<string>();

    const selectedTier = useAtomValue(selectedTierAtom);

    const columnFilters = useMemo(() => {
        const filters = [];

        if (selectedSubthemesIds.length) {
            filters.push({ id: "subtheme", value: selectedSubthemesIds });
        }

        if (selectedFrameworkIds.length) {
            filters.push({ id: "frameworks", value: selectedFrameworkIds });
        }

        if (selectedTier) {
            filters.push({ id: "tier", value: selectedTier });
        }

        return filters;
    }, [selectedSubthemesIds, selectedFrameworkIds, selectedTier]);

    useEffect(() => {
        setPagination({ ...pagination, pageIndex: 0 });
    }, [
        selectedSubthemesIds,
        selectedFrameworkIds,
        setPagination,
        selectedTier,
    ]);

    const table = useReactTable<Indicator>({
        data: (data?.sort((i1, i2) => i1?.order - i2?.order) ||
            []) as Indicator[],
        columns: inactiveIndicatorsTableColumns(),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onRowSelectionChange: setRowSelection,
        onPaginationChange: setPagination,
        state: {
            rowSelection,
            globalFilter: searchFilter,
            columnFilters: columnFilters,
            columnVisibility: { tier: false },
            pagination,
        },
        autoResetPageIndex: false,
    });

    const selectedIndicators = table
        .getSelectedRowModel()
        .flatRows.map((row) => row.original);

    const onSetRelevant = () => {
        const ids = selectedIndicators.map(
            (d) => d.entityConfig?.id,
        ) as string[];
        if (!ids) {
            return;
        }
        mutate(
            {
                indicatorEntityConfigIds: ids,
                set: {
                    active: true,
                    userId: undefined,
                },
            },
            {
                onSuccess: () => {
                    table.resetRowSelection();
                    setOpen(false);
                },
            },
        );
        table.resetRowSelection();
    };

    return (
        <CenterModal open={open} onOpenChange={setOpen} modal={false}>
            <CenterModalContent
                title={t("not_relevant_modal_title")}
                actions={
                    <ButtonRegular
                        text={t("set_relevant", {
                            count: selectedIndicators.length,
                        })}
                        onClick={onSetRelevant}
                        loading={isPending}
                        disabled={selectedIndicators.length === 0}
                    />
                }
            >
                <div className="w-[920px]">
                    {data ? (
                        <div className="flex flex-col gap-4">
                            <SearchBar
                                searchString={searchFilter}
                                setSearchString={setSearchFilter}
                                className="w-full"
                            />
                            <DataTable
                                table={table}
                                rowClassName={() => "cursor-pointer"}
                            />
                        </div>
                    ) : (
                        <div>Error, refresh ...</div>
                    )}
                </div>
            </CenterModalContent>
        </CenterModal>
    );
}
